import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  QaiTableComponent,
  TableRow,
  QaiLoadingComponent,
  QaiBadgeKind,
  KebabMenuItem,
  SortDirection,
  QaiSearchableSelectOption,
  QaiSearchableSelectComponent,
  HeaderConfig,
} from '@qai/ui';
import { IAssignment } from 'src/app/interfaces/self-serve/assignment-manager/iassignment';
import { AssignmentsService } from 'src/app/services/self-serve/assignments.service';
import { ActivatedRoute, Router } from '@angular/router';
import { formatLongMonthDate } from 'src/app/utils/date-parser.util';
import { PaginationControllerService } from 'src/app/services/self-serve/pagination-controller.service';
import { AssignmentStatusValueEnum, AssignmentStatusLabelEnum} from 'src/app/utils/enums/self-serve/assignment-type.enum';
import { SelfServeLocalStorageUtil } from 'src/app/utils/local-storage.util';
import { transformLabel } from 'src/app/utils/string-replacements';

const SIMULATION_TABLE_HEADERS: HeaderConfig[] = [
  {
    name: 'Name',
    sortable: true,
    columnWidth: '15%',
  },
  {
    name: 'Assignment start date',
    sortable: true,
    columnWidth: '13%',
  },
  {
    name: 'Assignment end date',
    sortable: true,
    columnWidth: '13%',
  },
  {
    name: 'Learner assigned',
    sortable: true,
    columnWidth: '13%',
  },
  {
    name: 'Product',
    sortable: true,
    columnWidth: '15%',
  },
  {
    name: 'Assignment type',
    sortable: true,
    columnWidth: '13%',
  },
  {
    name: 'Assignment status',
    sortable: true,
    columnWidth: '10%',
  },
];

@Component({
  selector: 'app-assignments',
  standalone: true,
  templateUrl: './assignments.component.html',
  styleUrls: ['./assignments.component.scss'],
  imports: [CommonModule, QaiTableComponent, QaiLoadingComponent, QaiSearchableSelectComponent],
})
export class AssignmentsComponent implements OnInit {
  tableData: TableRow[] = [];
  statusOptions: QaiSearchableSelectOption[] = [
    { label: AssignmentStatusLabelEnum.DRAFT, value: AssignmentStatusValueEnum.DRAFT },
    { label: AssignmentStatusLabelEnum.STAGED, value: AssignmentStatusValueEnum.STAGED },
    { label: AssignmentStatusLabelEnum.ACTIVE, value: AssignmentStatusValueEnum.ACTIVE },
    { label: AssignmentStatusLabelEnum.COMPLETE, value: AssignmentStatusValueEnum.COMPLETE },
    { label: AssignmentStatusLabelEnum.CLOSED, value: AssignmentStatusValueEnum.CLOSED },
    { label: AssignmentStatusLabelEnum.ARCHIVED, value: AssignmentStatusValueEnum.ARCHIVED },
  ];
  
  // Filter shows empty initially
  selectedStatusValues: string[] = [];

  // Default filter (all except Archived) - used for table display
  defaultValues: string[] = [
    AssignmentStatusValueEnum.DRAFT,
    AssignmentStatusValueEnum.STAGED,
    AssignmentStatusValueEnum.ACTIVE,
    AssignmentStatusValueEnum.COMPLETE,
    AssignmentStatusValueEnum.CLOSED,
  ];

  allStatusValues: string[] = [
    AssignmentStatusValueEnum.DRAFT,
    AssignmentStatusValueEnum.STAGED,
    AssignmentStatusValueEnum.ACTIVE,
    AssignmentStatusValueEnum.COMPLETE,
    AssignmentStatusValueEnum.CLOSED,
    AssignmentStatusValueEnum.ARCHIVED,
  ];
 
  allAssignments: IAssignment[] = [];
  clearOption = { enable: true, label: 'Clear' };
  visibleOptionsSelectionMode = {
    enable: true,
    selectAllOption: { value: 'selectAll', label: 'Select all' },
    deselectAllOption: { value: 'deselectAll', label: 'Deselect all' }
  };
  readonly headers: HeaderConfig[] = SIMULATION_TABLE_HEADERS;
  isLoading = true;
  initialSortColumn: string = SIMULATION_TABLE_HEADERS[2].name;
  initialSortDirection: SortDirection = 'desc';
  options: { label: string; value: string }[] = [];
  @Input() selectedAccount: { label: string; value: string } | undefined;
  @Input() selectedAccountUuid: string = '';

  @Output() deleteAssignment = new EventEmitter<any>();
  @Output() duplicateAssignment = new EventEmitter<{
    id: string;
    name: string;
  }>();

  currentPage: number = 1;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private assignmentsService: AssignmentsService,
    private paginationControllerService: PaginationControllerService
  ) {}

  ngOnInit(): void {
    this.initializePagination();
  }

  private initializePagination(): void {
    this.currentPage = this.paginationControllerService.getAssignmentsCurrentPage();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedAccount']) {
      this.loadAssignments();
    }
    const savedFilters = SelfServeLocalStorageUtil.getSelectedStatusFilters([]);
    if (savedFilters.length > 0) {
      this.selectedStatusValues = savedFilters;
    }
  }
  
  onStatusFilterChange(selectedStatuses: string | string[]): void {
    const incomingStatuses = Array.isArray(selectedStatuses)
      ? selectedStatuses
      : [selectedStatuses];

    const normalizedStatuses = incomingStatuses.map(s => s.toLowerCase());

    this.selectedStatusValues = normalizedStatuses;

    SelfServeLocalStorageUtil.setSelectedStatusFilters(normalizedStatuses);

    // If no filter selected, show default (all except Archived)
    // Otherwise, filter by selected statuses
    const statusesToFilter = normalizedStatuses.length === 0 
      ? this.defaultValues.map(s => s.toLowerCase())
      : normalizedStatuses;

    const filteredData = this.allAssignments.filter(a =>
      statusesToFilter.includes((a.status || 'staged').toLowerCase())
    );

    this.currentPage = 1;
    this.paginationControllerService.setAssignmentsCurrentPage(this.currentPage);
    this.tableData = this.transformApiDataToTableRows(filteredData);
  }

  public loadAssignments() {
    this.isLoading = true;

    const accountUuid = this.selectedAccount?.value || this.selectedAccountUuid;
    if (!accountUuid) {
      console.error('No valid account UUID found to load assignments.');
      this.isLoading = false;
      return;
    }

    this.assignmentsService.findAllAssignments(accountUuid).subscribe({
      next: (apiData: IAssignment[]) => {
        this.allAssignments = apiData;
        const savedFilters = SelfServeLocalStorageUtil.getSelectedStatusFilters([]);
        
        // If filters exist → apply them, otherwise use default (all except Archived)
        const statusesToFilter = savedFilters.length > 0
          ? savedFilters
          : this.defaultValues.map(s => s.toLowerCase());

        const filteredData = this.allAssignments.filter(a =>
          statusesToFilter.includes((a.status || 'staged').toLowerCase())
        );
    
        this.tableData = this.transformApiDataToTableRows(filteredData);
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Failed to load assignments:', err);
        this.isLoading = false;
      },
    });
  }

  onPageChanged(event: { page: number }): void {
    this.currentPage = event.page;
    
    this.paginationControllerService.setAssignmentsCurrentPage(this.currentPage);
  }

  private transformApiDataToTableRows(apiData: IAssignment[]): TableRow[] {
    return apiData.map((assignment) => {
      const kebabOptions = this.getKebabOptions();
  
      return this.createAssignmentTableRow(
        assignment.uuid,
        assignment.name,
        formatLongMonthDate(assignment.startDate),
        formatLongMonthDate(assignment.endDate),
        this.formatUserCount(Number(assignment.userCount)),
        assignment.moduleName,
        transformLabel(assignment.moduleAssignmentType || ''),
        this.getStatusBadge(assignment.status),
        kebabOptions
      );
    });
  }

  private getKebabOptions(): KebabMenuItem[] {
    return [
      { type: 'click', text: 'Delete' },
      { type: 'click', text: 'Duplicate' },
    ];
  }

  onTableRowAction(event: {
    action: KebabMenuItem;
    rowId: string | number;
    rowData: TableRow;
    event: Event;
  }) {
    if (!event.action.text) return;
    switch (event.action.text.toLowerCase()) {
      case 'delete':
        this.deleteAssignment.emit({ id: event.rowId, rowData: event.rowData });
        break;
      case 'duplicate':
        const nameCell = event.rowData['Name'];
        const name = (nameCell as { value: string }).value;
        this.duplicateAssignment.emit({ id: event.rowId as string, name });
        break;
      default:
        break;
    }
  }

  onTableRowClick(event: {
    rowId: string | number;
    rowData: TableRow;
    event: Event;
  }) {
    this.handleEdit(event.rowId);
  }

  private handleEdit(rowId: string | number) {
    this.router.navigate(['assignments', rowId], {
      relativeTo: this.route,
    });
  }

  createAssignmentTableRow(
    id: string | number,
    name: string,
    assignmentStartDate: string,
    assignmentEndDate: string,
    userCount: string,
    moduleName: string,
    assignmentType: string,
    assignmentStatus: { kind: QaiBadgeKind; label: string },
    options: KebabMenuItem[],
  ): TableRow {
    const row = {
      id,
      [SIMULATION_TABLE_HEADERS[0].name]: { type: 'text', value: name },
      [SIMULATION_TABLE_HEADERS[1].name]: {
        type: 'text',
        value: assignmentStartDate,
      },
      [SIMULATION_TABLE_HEADERS[2].name]: { type: 'text', value: assignmentEndDate },
      [SIMULATION_TABLE_HEADERS[3].name]: { type: 'text', value: userCount },
      [SIMULATION_TABLE_HEADERS[4].name]: {
        type: 'text',
        value: moduleName,
      },
      [SIMULATION_TABLE_HEADERS[5].name]: {
        type: 'badge',
        shape: 'rectangle',
        kind: 'gray',
        label: assignmentType,
      },
      [SIMULATION_TABLE_HEADERS[6].name]: {
        type: 'badge',
        shape: 'round',
        kind: assignmentStatus.kind,
        label: assignmentStatus.label,
      },
    } as const;
    return { ...row, options } as unknown as TableRow;
  }

  private getStatusBadge(status: string | null): {
    kind: QaiBadgeKind;
    label: string;
  } {
    if (!status) {
      return { kind: 'yellow', label: 'Staged' };
    }

    switch (status.toLowerCase()) {
      case 'staged':
        return { kind: 'yellow', label: 'Staged' };
      case 'active':
        return { kind: 'green', label: 'Active' };
      case 'complete':
        return { kind: 'gray-blue', label: 'Complete' };
      case 'closed':
        return { kind: 'gray-blue-outline', label: 'Closed' };
      case 'draft':
        return { kind: 'yellow', label: 'Draft' };
      default:
        return { kind: 'yellow', label: transformLabel(status) };
    }
  }

  private formatUserCount(userCount: number): string {
    if (!userCount || userCount === 0) {
      return 'None';
    }
    return userCount === 1
      ? `${userCount} learner`
      : `${userCount} learners`;
  }

  get hasNoAssignments(): boolean {
    return this.allAssignments.length === 0;
  }
}